/**
 * ActiveEventsBanner — shows any period events whose [ts_start, ts_end]
 * window covers NOW. Single banner above the dashboard so the user
 * sees "🟦 Тест ML direction filter — started 2 days ago" before
 * reading metrics.
 *
 * Hidden when nothing is active. Point events (ts_end NULL) don't
 * surface here — the EquityCurve overlay shows them as vertical lines.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EVENT_TONE } from '@/pages/app/Events'
import { useEvents } from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'

export function ActiveEventsBanner() {
  const { t } = useTranslation('app')
  // Window: events whose start is at most 30d ago — narrow lookup so
  // we don't pay for the full history every dashboard render.
  const fromIso = useMemo(
    () => new Date(Date.now() - 30 * 86400_000).toISOString(),
    [],
  )
  const { data } = useEvents({ from: fromIso, limit: 50 })

  const active = useMemo(() => {
    if (!data) return []
    const now = Date.now()
    return data.items.filter((ev) => {
      if (!ev.ts_end) return false
      const start = new Date(ev.ts_start).getTime()
      const end = new Date(ev.ts_end).getTime()
      return start <= now && now <= end
    })
  }, [data])

  if (active.length === 0) return null

  return (
    <div className="mb-4 space-y-1">
      {active.map((ev) => {
        const tone = EVENT_TONE[ev.type]
        return (
          <Link
            key={ev.id}
            to="/app/events"
            className={cn(
              'block px-3 py-2 border text-sm',
              tone.bg, tone.text, 'border-current/30 hover:brightness-110',
            )}
          >
            <span className="font-semibold">
              {t(`events.type.${ev.type}`)}:
            </span>{' '}
            {ev.title}
            {ev.description && (
              <span className="ml-2 text-xs opacity-80">
                — {ev.description.length > 80
                    ? ev.description.slice(0, 78) + '…'
                    : ev.description}
              </span>
            )}
          </Link>
        )
      })}
    </div>
  )
}
